// frontend/src/components/forms/MaintenanceLogForm.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner'; // Para indicar carga de activos
import { Col, Row } from 'react-bootstrap';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Definir interfaz para Activo (solo lo necesario para el select)
interface AssetOption {
  id: number;
  name: string;
  identifier: string;
}

interface MaintenanceLogFormProps {
  onLogCreated?: () => void; // Callback opcional
}

export function MaintenanceLogForm({ onLogCreated }: MaintenanceLogFormProps) {
  // Estado para los datos del formulario
  const [assetId, setAssetId] = useState<string>(''); // Guardamos el ID como string para el <select>
  const [maintenanceType, setMaintenanceType] = useState<'Correctivo' | 'Preventivo'>('Correctivo');
  const [description, setDescription] = useState('');
  const [costParts, setCostParts] = useState<string>('0'); // Usar string para input type number
  const [costLabor, setCostLabor] = useState<string>('0');
  const [reportedBy, setReportedBy] = useState(''); // Opcional
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  // Estado para cargar los activos y manejar UI
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Cargar lista de activos para el desplegable
  useEffect(() => {
    const fetchAssets = async () => {
      setLoadingAssets(true);
      setError(null); // Limpiar errores previos
      try {
        const response = await fetch(`${API_BASE_URL}/api/data/assets`);
        if (!response.ok) throw new Error('No se pudieron cargar los activos.');
        const data: AssetOption[] = await response.json();
        setAssets(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido cargando activos.');
        console.error("Error fetching assets for form:", err);
      } finally {
        setLoadingAssets(false);
      }
    };
    fetchAssets();
  }, []); // Se ejecuta solo una vez al montar

  // Handler para el input de archivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachmentFile(e.target.files[0]);
    } else {
      setAttachmentFile(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!assetId) {
        setError("Debes seleccionar un activo.");
        return;
    }
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    // 1. Prepara el objeto de datos SIEMPRE
    const logDataObject = {
      asset_id: parseInt(assetId, 10),
      maintenance_type: maintenanceType,
      description,
      cost_parts: parseFloat(costParts) || 0,
      cost_labor: parseFloat(costLabor) || 0,
      reported_by: reportedBy || null
    };

    // 2. Prepara cuerpo y cabeceras
    let requestBody: FormData | string;
    let requestHeaders: HeadersInit = {};

    if (attachmentFile) {
      const formData = new FormData();
      Object.entries(logDataObject).forEach(([key, value]) => {
        formData.append(key, value !== null && value !== undefined ? String(value) : '');
      });
      formData.append('attachmentFile', attachmentFile, attachmentFile.name);
      requestBody = formData;
      // No añadir Content-Type header aquí
    } else {
      requestBody = JSON.stringify(logDataObject);
      requestHeaders['Content-Type'] = 'application/json';
    }

    // 3. Realiza el fetch (dentro del try...catch)
    try {
      const response = await fetch(`${API_BASE_URL}/api/data/maintenance-logs`, {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody,
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Error al registrar el mantenimiento.');
      }

      setSuccess(`Registro de mantenimiento creado con ID: ${result.id}`);
      setAssetId('');
      setMaintenanceType('Correctivo');
      setDescription('');
      setCostParts('0');
      setCostLabor('0');
      setReportedBy('');
      setAttachmentFile(null);
      onLogCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error desconocido.');
      console.error("Error en MaintenanceLogForm submit:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h4>Registrar Mantenimiento</h4>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Form.Group className="mb-3" controlId="maintAssetId">
        <Form.Label>Activo</Form.Label>
        <Form.Select
          value={assetId}
          onChange={(e) => setAssetId(e.target.value)}
          required
          disabled={loadingAssets}
          aria-label="Seleccionar Activo"
        >
          <option value="">{loadingAssets ? 'Cargando...' : '-- Selecciona un Activo --'}</option>
          {assets.map(asset => (
            <option key={asset.id} value={asset.id}>
              {asset.name} ({asset.identifier})
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3" controlId="maintType">
        <Form.Label>Tipo</Form.Label>
        <Form.Select
          value={maintenanceType}
          onChange={(e) => setMaintenanceType(e.target.value as 'Correctivo' | 'Preventivo')}
          required
        >
          <option value="Correctivo">Correctivo</option>
          <option value="Preventivo">Preventivo</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3" controlId="maintDescription">
        <Form.Label>Descripción</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          placeholder="Describe el trabajo realizado..."
        />
      </Form.Group>

      <Row>
        <Col>
          <Form.Group className="mb-3" controlId="maintCostParts">
            <Form.Label>Costo Repuestos ($)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={costParts}
              onChange={(e) => setCostParts(e.target.value)}
              placeholder="0.00"
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3" controlId="maintCostLabor">
            <Form.Label>Costo Mano Obra ($)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={costLabor}
              onChange={(e) => setCostLabor(e.target.value)}
              placeholder="0.00"
            />
          </Form.Group>
        </Col>
      </Row>

       <Form.Group className="mb-3" controlId="maintReportedBy">
         <Form.Label>Reportado/Realizado Por (Opcional)</Form.Label>
         <Form.Control
           type="text"
           value={reportedBy}
           onChange={(e) => setReportedBy(e.target.value)}
           placeholder="Tu nombre o iniciales"
         />
       </Form.Group>

      {/* === CAMPO DE ADJUNTO === */}
      <Form.Group controlId="formFile" className="mb-3">
        <Form.Label>Adjunto (JPG, PNG, PDF - Opcional)</Form.Label>
        <Form.Control
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={handleFileChange}
        />
      </Form.Group>

      <Button variant="primary" type="submit" disabled={isSubmitting || loadingAssets}>
        {isSubmitting && (
          <>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
            Registrando...
          </>
        )}
        {!isSubmitting && 'Registrar Mantenimiento'}
      </Button>
    </Form>
  );
}

// Necesitarás importar Row y Col de react-bootstrap si no lo has hecho
// import Row from 'react-bootstrap/Row';
// import Col from 'react-bootstrap/Col';